import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { useCreateMailbox } from '../hooks/useMailboxes';

export default function AddMailboxDialog({ open, onClose }) {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    imap: {
      host: '',
      port: 993,
      secure: true
    },
    smtp: {
      host: '',
      port: 465,
      secure: true
    },
    credentials: {
      user: '',
      pass: ''
    }
  });

  const [error, setError] = useState('');
  const createMailboxMutation = useCreateMailbox();

  const handleChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async () => {
    setError('');
    
    // Validation
    if (!formData.email || !formData.imap.host || !formData.smtp.host || !formData.credentials.user || !formData.credentials.pass) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await createMailboxMutation.mutateAsync(formData);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create mailbox');
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      displayName: '',
      imap: {
        host: '',
        port: 993,
        secure: true
      },
      smtp: {
        host: '',
        port: 465,
        secure: true
      },
      credentials: {
        user: '',
        pass: ''
      }
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Email Account</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          {/* Basic Information */}
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
            Account Information
          </Typography>
          <TextField
            label="Email Address"
            type="email"
            required
            fullWidth
            value={formData.email}
            onChange={(e) => handleChange(null, 'email', e.target.value)}
            placeholder="user@example.com"
          />
          <TextField
            label="Display Name"
            fullWidth
            value={formData.displayName}
            onChange={(e) => handleChange(null, 'displayName', e.target.value)}
            placeholder="My Work Email"
          />

          {/* IMAP Settings */}
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mt: 2 }}>
            IMAP Settings (Incoming Mail)
          </Typography>
          <TextField
            label="IMAP Host"
            required
            fullWidth
            value={formData.imap.host}
            onChange={(e) => handleChange('imap', 'host', e.target.value)}
            placeholder="imap.gmail.com"
          />
          <TextField
            label="IMAP Port"
            type="number"
            required
            fullWidth
            value={formData.imap.port}
            onChange={(e) => handleChange('imap', 'port', parseInt(e.target.value) || 993)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.imap.secure}
                onChange={(e) => handleChange('imap', 'secure', e.target.checked)}
              />
            }
            label="Use SSL/TLS"
          />

          {/* SMTP Settings */}
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mt: 2 }}>
            SMTP Settings (Outgoing Mail)
          </Typography>
          <TextField
            label="SMTP Host"
            required
            fullWidth
            value={formData.smtp.host}
            onChange={(e) => handleChange('smtp', 'host', e.target.value)}
            placeholder="smtp.gmail.com"
          />
          <TextField
            label="SMTP Port"
            type="number"
            required
            fullWidth
            value={formData.smtp.port}
            onChange={(e) => handleChange('smtp', 'port', parseInt(e.target.value) || 465)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.smtp.secure}
                onChange={(e) => handleChange('smtp', 'secure', e.target.checked)}
              />
            }
            label="Use SSL/TLS"
          />

          {/* Credentials */}
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mt: 2 }}>
            Login Credentials
          </Typography>
          <TextField
            label="Username"
            required
            fullWidth
            value={formData.credentials.user}
            onChange={(e) => handleChange('credentials', 'user', e.target.value)}
            placeholder="username"
          />
          <TextField
            label="Password"
            type="password"
            required
            fullWidth
            value={formData.credentials.pass}
            onChange={(e) => handleChange('credentials', 'pass', e.target.value)}
            placeholder="password"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMailboxMutation.isPending}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={createMailboxMutation.isPending}
          startIcon={createMailboxMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {createMailboxMutation.isPending ? 'Adding...' : 'Add Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
